// TypeScript interfaces for structured technique explanations

export interface ExplanationExample {
  input?: string;
  problem?: string;
  calculation: string;
  result: string;
  highlight?: boolean;
  visual?: string;
  note?: string;
  reasoning?: string;
}

export interface ConditionalBranch {
  condition: string;
  action: string;
  formula?: string;
  conversion?: Record<string, string>;
  examples?: ExplanationExample[];
}

export interface SpecialCase {
  title: string;
  example?: string;
  [key: string]: any; // Flexible for case-specific fields
}

// Base step properties shared by all step types
interface BaseStep {
  type: "step" | "formula" | "condition" | "tip";
  number: number;
  title: string;
  content?: string;
  formula?: string | string[];
  memoryTrick?: string;
  important?: string;
  examples?: ExplanationExample[];
}

// Regular step with optional substeps
export interface Step extends BaseStep {
  type: "step";
  description?: string;
}

// Formula step with visual representation
export interface FormulaStep extends BaseStep {
  type: "formula";
  description: string;
  visual?: {
    type: "matrix" | "equation" | "diagram";
    content: string;
  };
}

// Conditional step with if/then branches
export interface ConditionalStep extends BaseStep {
  type: "condition";
  branches: ConditionalBranch[];
}

export type ExplanationStep = Step | FormulaStep | ConditionalStep;

// Full structured explanation
export interface StructuredExplanation {
  overview: string;
  setup?: {
    system?: string[];
    [key: string]: any;
  };
  applicability?: {
    condition: string;
    examples: string[];
  };
  steps: ExplanationStep[];
  specialCases?: SpecialCase[];
  memoryTrick?: string;
  whyItWorks?: string;
}

// Type guard functions
export function isStructuredExplanation(
  explanation: any
): explanation is StructuredExplanation {
  return (
    typeof explanation === "object" &&
    explanation !== null &&
    typeof explanation.overview === "string" &&
    Array.isArray(explanation.steps)
  );
}

export function isStep(step: ExplanationStep): step is Step {
  return step.type === "step";
}

export function isFormulaStep(step: ExplanationStep): step is FormulaStep {
  return step.type === "formula";
}

export function isConditionalStep(
  step: ExplanationStep
): step is ConditionalStep {
  return step.type === "condition";
}
